import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { getLogo } from "src/shared/services/services";
import PrintAppFind from "src/print-configuration/PrintAppFind";
import { fetchPrintConfig } from "../configurations/commonPrint/api/printConfig";
import { defaultConfig, PrintLayout } from "../configurations/commonPrint/hooks/usePrintConfig";
import { DateFormat, TimeFormat } from "../configurations/types/configuration.types";

export const toBoolean = (value: any) => value === true || value === "true";

// ── All your existing interfaces (unchanged) ──────────────────────────────────
export interface UserDetailsResponse { user_details: UserDetails; }
export interface UserDetails {
  addresses: Address;
  businessLocation: string;
  businessType: string;
  createdOn: string;
  dob: string;
  emails: { email: string; isPrimary: boolean }[];
  firstName: string;
  middleName?: string;
  lastName: string;
  orgArabicName?: string;
  orgId: string;
  orgName: string;
  phones: { phone: string; isPrimary: boolean }[];
  profileImageUrl: string | null;
  businessLogo: string;
  role: string[];
  status: "Active" | "Inactive" | string;
  themes: ThemeSettings;
  userName: string;
  companyLogo: string | null;
  printProductLimit?: string;
  companyLogoAPP?: string | null;
  permissions: any[];
}
export interface Address {
  addressLine1: string;
  addressLine2?: string;
  country: string;
  postalCode: string;
  state: string;
}
export interface ThemeSettings {
  backgroundColour: string;
  fontColour: string;
  fontFamily: string;
}
export interface Configration {
  printType: "print:2" | "print:1" | "print:3";
  tableDefaultSize: number;
  copyQty: number;
  billResetPeriod?: string;
  billNoEnabled: boolean;
  foodTypes?: string[];
  shiftTypes?: string[];
  termsAndConditionsEnglish?: string;
  termsAndConditionsArabic?: string;
  enableTermsAndConditions?: boolean;
  contactQrCode: string;
  locationQrCode: string;
  isProductsNotesEnabled: boolean;
  isDefaultTabsEnabled: boolean;
  deliveryFeeEnabled?: boolean;
  productEditEnabled?: boolean;
  parcelFeeEnabled?: boolean;
  tipsEnabled?: boolean;
  cardPayment?: boolean;
  businessOperationEnable?: boolean;
  businessOperationType?: "Day" | "Shift";
  pdfProductLimit?: string;
  showOutstandingInPdf?: boolean;
  customBusinessName?: string;
  printProductLimit?: string;
  orderTableType?: "Table Type:1" | "Table Type:2";
  appPrinter?: boolean;
  contact_qr_codeAWS?: string;
  location_qr_codeAWS?: string;
  appPrinterEnabled?: boolean | string;
  orderIdEnabled: boolean;
  layoutId?: string;
  printLayoutId?: string;
  customEnabledPrint?: boolean;
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  orderEditingEnabled?: boolean;
}
export interface SubscriptionInfo {
  expiry_date?: string;
  grace_end_date?: string;
  message?: string;
  status: "active" | "inactive" | "expired" | "grace_period" | "blocked";
  warning?: string | null;
  daysRemaining?: number;
}

// ── NEW: helper to read the session token ─────────────────────────────────────
const getSessionToken = (): string | null =>
  sessionStorage.getItem("jsonwebtoken"); // 🔑 change key to match yours

// ── Context types ─────────────────────────────────────────────────────────────
interface UserContextProps {
  user: UserDetails | null;
  setUser: (user: UserDetails | null) => void;
  userConfigration: Configration;
  setConfigurations: (config: Configration) => void;
  subscriptionInfo: SubscriptionInfo | null;
  setSubscriptionInfo: (info: SubscriptionInfo | null) => void;
  printConfig: PrintLayout | undefined;
  /** NEW — call this to force-refresh all derived context state */
  refreshUserContext: () => void;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

// ─────────────────────────────────────────────────────────────────────────────
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserDetails | null>(null);

  // NEW: refresh counter — incrementing it re-triggers all dependent useEffects
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshUserContext = useCallback(() => setRefreshKey((k) => k + 1), []);


  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [userConfigration, setUserConfigration] = useState<Configration>({
    printType: "print:1",
    tableDefaultSize: 10,
    copyQty: 1,
    billResetPeriod: "noreset",
    billNoEnabled: false,
    foodTypes: ["Veg", "Non-Veg"],
    contactQrCode: "",
    locationQrCode: "",
    termsAndConditionsEnglish: "",
    termsAndConditionsArabic: "",
    enableTermsAndConditions: false,
    isDefaultTabsEnabled: false,
    isProductsNotesEnabled: false,
    deliveryFeeEnabled: false,
    parcelFeeEnabled: false,
    tipsEnabled: false,
    printProductLimit: "5",
    customBusinessName: "Grocery",
    orderTableType: "Table Type:1",
    contact_qr_codeAWS: "",
    location_qr_codeAWS: "",
    appPrinterEnabled: false,
    orderIdEnabled: true,
    customEnabledPrint: false,
    printLayoutId: "",
    cardPayment: false,
    dateFormat: "DD/MM/YYYY",
    timeFormat: "12h",
  });
  const [printConfig, setPrintConfig] = useState<PrintLayout>();

  const usePrinterList = () => {
    if (Boolean(userConfigration.appPrinterEnabled) === true) {

      const { newPrinterList } = PrintAppFind(); // always called unconditionally
      const hasToken = Boolean(getSessionToken());
      return hasToken ? newPrinterList : [];     // filter result, not the call
    }
  };

  // Token-gated printer list (no token → [])
  const NewPrinterList = usePrinterList() ?? "";

  // ── setConfigurations (unchanged logic, same as before) ───────────────────
  const setConfigurations = (config: Configration) => {
    if (config.contactQrCode) {
      const base64 = config.contactQrCode.startsWith("data:image")
        ? config.contactQrCode
        : `data:image/png;base64,${config.contactQrCode}`;
      config = { ...config, contactQrCode: base64 };
    }
    if (config.locationQrCode) {
      const base64 = config.locationQrCode.startsWith("data:image")
        ? config.locationQrCode
        : `data:image/png;base64,${config.locationQrCode}`;
      config = { ...config, locationQrCode: base64 };
    }
    if (config) {
      setUserConfigration((prev) => ({ ...prev, ...config }));
    }
  };

  // ── businessType → orderTableType / printType ─────────────────────────────
  // refreshKey added so a manual refresh also re-evaluates this
  useEffect(() => {
    const businessType = user?.businessType || "";
    const isGrocery = businessType.toLowerCase() === "grocery";
    setUserConfigration((prev) => ({
      ...prev,
      orderTableType: isGrocery ? "Table Type:2" : prev.orderTableType,
    }));
  }, [user?.businessType, refreshKey]);


  // ── Printer / QR code AWS URL resolution ──────────────────────────────────
  useEffect(() => {
    const cleanBase64 = (value: string | undefined) => {
      if (!value) return "";
      return value.replace(/^data:image\/[a-zA-Z]+;base64,/, "");
    };
    const imageUrl = cleanBase64(user?.businessLogo);
    const qrCodeUrl = cleanBase64(userConfigration?.contactQrCode);
    const locationQrCodeUrl = cleanBase64(userConfigration?.locationQrCode);

    if (
      NewPrinterList.length > 0 &&
      toBoolean(userConfigration?.appPrinterEnabled) === true
    ) {
      setUser((prev) =>
        prev
          ? { ...prev, companyLogoAPP: `${process.env.REACT_APP_IMAGE_PATH}/${imageUrl}` }
          : prev
      );
      setUserConfigration((prev) => ({
        ...prev,
        contact_qr_codeAWS: `${process.env.REACT_APP_IMAGE_PATH}/${qrCodeUrl}`,
        location_qr_codeAWS: `${process.env.REACT_APP_IMAGE_PATH}/${locationQrCodeUrl}`,
        appPrinter: true,
      }));
    } else if (toBoolean(userConfigration?.appPrinterEnabled) === false) {
      setUser((prev) => (prev ? { ...prev, companyLogoAPP: user?.companyLogo } : prev));

      getLogo(qrCodeUrl)
        .then((response: any) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const imageUrl = URL.createObjectURL(blob);
          setUserConfigration((prev) => ({
            ...prev,
            contact_qr_codeAWS: imageUrl,
            appPrinter: false,
          }));
        })
        .catch((err) => console.error(err));

      getLogo(locationQrCodeUrl)
        .then((response: any) => {
          const blob = new Blob([response.data], { type: "image/png" });
          const imageUrl = URL.createObjectURL(blob);
          setUserConfigration((prev) => ({
            ...prev,
            location_qr_codeAWS: imageUrl,
            appPrinter: false,
          }));
        })
        .catch((err) => console.error(err));
    }
  }, [
    NewPrinterList,
    userConfigration?.contactQrCode,
    userConfigration?.locationQrCode,
    user?.companyLogo,
    userConfigration?.appPrinterEnabled,
    user?.businessLogo,
    refreshKey, // re-run on manual refresh
  ]);

  // ── Custom print layout fetch ──────────────────────────────────────────────
  useEffect(() => {
    if (user?.orgName && userConfigration?.customEnabledPrint) {
      fetchPrintConfig()
        .then((res) => {
          const getData: PrintLayout[] = res.data;
          const customLayout = [defaultConfig, ...getData];
          const isWorkingLayout = customLayout.find(
            (layout) => layout._id.$oid === userConfigration.printLayoutId
          );
          setPrintConfig(isWorkingLayout);
        })
        .catch((error) => console.error("Failed to fetch print config:", error));
    }
  }, [user?.orgName, userConfigration?.customEnabledPrint, userConfigration.printLayoutId, refreshKey]);

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        userConfigration,
        setConfigurations,
        subscriptionInfo,
        setSubscriptionInfo,
        printConfig,
        refreshUserContext, // NEW — expose to consumers
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) throw new Error("useUser must be used within a UserProvider");
  return context;
};